import { useState } from "react";
import SectionCard from "./SectionCard";

type Props = {
  onChange: (msg: string) => void;
};

type Zone = {
  id: string;
  name: string;
  zips: string;
  surcharge: number;
  active: boolean;
};

const initialZones: Zone[] = [
  {
    id: "manhattan",
    name: "Manhattan",
    zips: "10001 · 10002 · 10003 · 10011 · 10014 · 10128",
    surcharge: 12,
    active: true,
  },
  {
    id: "brooklyn",
    name: "Brooklyn",
    zips: "11201 · 11205 · 11211 · 11215 · 11217 · 11231",
    surcharge: 0,
    active: true,
  },
  {
    id: "queens",
    name: "Queens",
    zips: "11101 · 11106 · 11377 · 11385",
    surcharge: 8,
    active: true,
  },
  {
    id: "bronx",
    name: "Bronx",
    zips: "10451 · 10454 · 10463",
    surcharge: 10,
    active: false,
  },
];

export default function ServiceZonesSection({ onChange }: Props) {
  const [zones, setZones] = useState<Zone[]>(initialZones);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ name: "", zips: "", surcharge: 0 });

  const toggleActive = (id: string) => {
    const zone = zones.find((z) => z.id === id);
    setZones((zs) => zs.map((z) => (z.id === id ? { ...z, active: !z.active } : z)));
    if (zone) {
      onChange(`${zone.name} ${zone.active ? "paused" : "activated"}`);
    }
  };

  const removeZone = (id: string) => {
    const zone = zones.find((z) => z.id === id);
    setZones((zs) => zs.filter((z) => z.id !== id));
    if (zone) onChange(`${zone.name} removed`);
  };

  const addZone = () => {
    if (!draft.name.trim()) return;
    const id = draft.name.trim().toLowerCase().replace(/\s+/g, "-") + "-" + Math.random().toString(36).slice(2, 5);
    setZones((zs) => [
      ...zs,
      {
        id,
        name: draft.name.trim(),
        zips: draft.zips.trim() || "—",
        surcharge: Number(draft.surcharge) || 0,
        active: true,
      },
    ]);
    setDraft({ name: "", zips: "", surcharge: 0 });
    setAdding(false);
    onChange(`${draft.name.trim()} added`);
  };

  return (
    <SectionCard
      title="Service zones"
      description="Pick the neighborhoods you cover and any travel surcharge per visit."
    >
      <ul className="divide-y divide-background-200">
        {zones.map((z) => (
          <li
            key={z.id}
            className="flex items-center gap-3 py-3 first:pt-0 last:pb-0"
          >
            <button
              onClick={() => toggleActive(z.id)}
              className={`w-10 h-6 rounded-full transition-colors cursor-pointer flex-shrink-0 ${
                z.active ? "bg-primary-500" : "bg-background-200"
              }`}
              aria-label={`Toggle ${z.name}`}
            >
              <span
                className={`block w-5 h-5 rounded-full bg-background-50 transition-transform mt-0.5 ${
                  z.active ? "translate-x-[18px]" : "translate-x-0.5"
                }`}
              ></span>
            </button>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-foreground-950">{z.name}</p>
              <p className="text-[11px] text-foreground-600 truncate">{z.zips}</p>
            </div>
            <div className="text-right whitespace-nowrap">
              <p className="text-xs font-semibold text-foreground-950">
                {z.surcharge === 0 ? "No surcharge" : `+$${z.surcharge}`}
              </p>
              <p className="text-[10px] text-foreground-500">per visit</p>
            </div>
            <button
              onClick={() => removeZone(z.id)}
              className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-600 hover:bg-background-100 cursor-pointer flex-shrink-0"
              aria-label={`Remove ${z.name}`}
            >
              <i className="ri-delete-bin-line text-sm"></i>
            </button>
          </li>
        ))}
      </ul>

      {adding ? (
        <div className="mt-4 p-4 rounded-lg bg-background-100">
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_120px] gap-2">
            <input
              type="text"
              value={draft.name}
              onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
              placeholder="Neighborhood"
              className="bg-background-50 text-sm text-foreground-900 rounded-md px-3 py-2 outline-none focus:ring-2 focus:ring-primary-300"
              autoFocus
            />
            <input
              type="text"
              value={draft.zips}
              onChange={(e) => setDraft((d) => ({ ...d, zips: e.target.value }))}
              placeholder="ZIP codes (10001 · 10002)"
              className="bg-background-50 text-sm text-foreground-900 rounded-md px-3 py-2 outline-none focus:ring-2 focus:ring-primary-300"
            />
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-foreground-500">$</span>
              <input
                type="number"
                value={draft.surcharge}
                onChange={(e) => setDraft((d) => ({ ...d, surcharge: Number(e.target.value) }))}
                placeholder="0"
                min={0}
                className="w-full bg-background-50 text-sm text-foreground-900 rounded-md pl-7 pr-3 py-2 outline-none focus:ring-2 focus:ring-primary-300"
              />
            </div>
          </div>
          <div className="flex items-center justify-end gap-2 mt-3">
            <button
              onClick={() => {
                setAdding(false);
                setDraft({ name: "", zips: "", surcharge: 0 });
              }}
              className="px-3 py-1.5 rounded-md text-xs text-foreground-700 hover:bg-background-200 cursor-pointer whitespace-nowrap"
            >
              Cancel
            </button>
            <button
              onClick={addZone}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-xs font-medium cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line"></i>
              Add zone
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setAdding(true)}
          className="mt-4 w-full inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-md border border-dashed border-background-300 text-foreground-700 hover:bg-background-100 text-xs font-medium cursor-pointer whitespace-nowrap"
        >
          <i className="ri-add-line"></i>
          Add a service zone
        </button>
      )}
    </SectionCard>
  );
}